import store from '~/store/store'
import Api from '~/store/idonia-api/api'
import $state from '~/plugins/uploader/upload-state'
import path from 'path'
import bus from '~/bus'
import dicomParser from 'dicom-parser'
import useLanguageTree from '~/commons/language'
import cache from '~/commons/cache'

const {t} = useLanguageTree()

const BANNED_EXTENSIONS = ['.exe', '.dll', '.log', '.inf', '.html', '.htm', '.cab', '.appimage', '.dat', '.db', '.ocx', '.vbs']
const DICOMSTUDY_WHITELISTED_EXTENSIONS = ['.pdf']
const UNKNOWN = -1
const BUNDLEDICOM = 0
const DICOM = 1
const IGNOREDICOM = 2

const async = require('async-es')

const q = async.queue(function (task, callback) {
  let payload = task.payload || {
    name: task.name,
    parent_id: task.parentID,
    ignore_notifications: task.ignoreNotifications
  }

  Api.postV2(task.endpoint, {
    data: payload
  }).then(response => {
    if (response.body && response.body[task.responseParam]) {
      callback(null, response.body[task.responseParam])
    } else {
      callback(response)
    }
  })
}, 6)

const preProcessFilesContainer = async (fileContainer, isDICOMDIR) => {
  return new Promise(async (resolve, reject) => {
    let fileObject = {
      name: '',
      isValid: true,
      isFolder: false,
      isFile: false,
      isDICOM: false,
      isDICOMDIR: false,
      hasDICOMDIR: false,
      hasDICOM: false,
      nestLevel: 0,
      childrenHasDICOM: false,
      file: {},
      children: []
    }

    switch (Object.prototype.toString.call(fileContainer)) {
      case '[object DataTransfer]':
        // FileList is an object that may or may not contain an items property
        // items is an iterator of DataTransferItem
        if (fileContainer.items) {
          let files = []
          for (let i = 0; i < fileContainer.items.length; i++) {
            let fileEntry = fileContainer.items[i].webkitGetAsEntry ? fileContainer.items[i].webkitGetAsEntry() : fileContainer.getAsFile()
            files.push(fileEntry)
          }
          fileObject = await preProcessFilesContainer(files)
        } else {
          fileObject = await preProcessFilesContainer(fileContainer.files)
        }
        break
      case '[object FileSystemDirectoryEntry]':
      case '[object DirectoryEntry]':
        let directoryReader = fileContainer.createReader()

        if (fileContainer.name.startsWith('.')) {
          fileObject.isValid = false
          break
        }

        let entries = await new Promise(async (resolve, reject) => {
          let entries = []
          let readEntries = () => {
            directoryReader.readEntries((results) => {
              if (!results.length) {
                resolve(entries)
              } else {
                entries = entries.concat(results)
                readEntries()
              }
            }, reject)
          }
          readEntries()
        })

        // entries is an Array of FileEntry or DirectoryEntry. It is reprocessed as Array
        fileObject = await preProcessFilesContainer(entries)
        fileObject.name = fileContainer.name.slice(0, 100)
        break
      case '[object FileList]':
        // FileList is an iterator of File.
        // This can have a flattened file list with relative paths stored in .webkitRelativePath
        // We have to transform this into an object
        let files = {}
        for (let i = 0; i < fileContainer.length; i++) {
          let fullPath = fileContainer[i].name
          if (fileContainer[i].webkitRelativePath) {
            fullPath = './' + fileContainer[i].webkitRelativePath
          }
          let dirname = path.dirname(fullPath)
          if (!files[dirname]) {
            files[dirname] = []
          }
          files[dirname].push(fileContainer[i])
        }
        fileObject = await preProcessFilesContainer(files)
        break
      case '[object Object]':
        // Object is a dictionary using path as key and an Array of File as values.
        // This has to be traversed recursively and ultimately process the Array
        let filesObjectsObjects = {}
        for (let dir in fileContainer) {
          let dirParts = dir.split(path.sep)
          dirParts.shift() // '.'

          if (dirParts.length > 0) {
            let folder = dirParts.shift() // Future new root
            let newPath = ['.'].concat(dirParts).join(path.sep)
            if (!filesObjectsObjects[folder]) {
              filesObjectsObjects[folder] = {}
            }
            if (filesObjectsObjects[folder][newPath]) {
              filesObjectsObjects[folder][newPath] = filesObjectsObjects[folder][newPath].concat(fileContainer[dir])
            } else {
              filesObjectsObjects[folder][newPath] = fileContainer[dir]
            }
          } else {
            fileObject = await preProcessFilesContainer(fileContainer[dir])
          }
        }
        for (let folder in filesObjectsObjects) {
          let result = await preProcessFilesContainer(filesObjectsObjects[folder])
          result.name = folder
          if (result.name.startsWith('.')) {
            result.isValid = false
            break
          }
          fileObject.name = result.name
          fileObject.isFolder = true
          fileObject.nestLevel = fileObject.nestLevel > result.nestLevel + 1 ? fileObject.nestLevel : result.nestLevel + 1
          if (!fileObject.hasDICOMDIR) {
            fileObject.hasDICOM = fileObject.hasDICOM || result.isDICOM
            fileObject.childrenHasDICOM = fileObject.childrenHasDICOM || result.childrenHasDICOM || result.hasDICOM
          }
          if (result.isDICOMDIR) {
            fileObject.childrenHasDICOM = false
            fileObject.hasDICOM = false
            fileObject.hasDICOMDIR = true
          }
          fileObject.children.push(result)
        }
        break
      case '[object Array]':
        fileObject.isFolder = true
        for (let index = 0; index < fileContainer.length; index++) {
          let result = await preProcessFilesContainer(fileContainer[index])
          if (!fileObject.isValid) {
            break // ???
          }
          fileObject.nestLevel = fileObject.nestLevel > result.nestLevel + 1 ? fileObject.nestLevel : result.nestLevel + 1
          if (!fileObject.hasDICOMDIR) {
            fileObject.hasDICOM = fileObject.hasDICOM || result.isDICOM
            fileObject.childrenHasDICOM = fileObject.childrenHasDICOM || result.childrenHasDICOM || result.hasDICOM
          }
          if (result.isDICOMDIR) {
            fileObject.childrenHasDICOM = false
            fileObject.hasDICOM = false
            fileObject.hasDICOMDIR = true
          }
          fileObject.children.push(result)
        }
        break
      case '[object Blob]':
        // Blob is binary data for a single file. It is converted to File and reprocessed
        fileObject = await preProcessFilesContainer(new File([fileContainer], fileContainer.name || 'blob', {type: fileContainer.type}))
        break
      case '[object FileSystemFileEntry]':
      case '[object FileEntry]':
        // FileEntry gets converted to File and reprocessed
        let file = await new Promise((resolve, reject) => {
          fileContainer.file((file) => {
            resolve(file)
          })
        })
        fileObject = await preProcessFilesContainer(file)
        break
      case '[object File]':
        let extension = path.extname(fileContainer.name).toLowerCase()
        if (BANNED_EXTENSIONS.indexOf(extension) > -1 || fileContainer.name.startsWith('.')) {
          fileObject.isValid = false
          break
        }
        fileObject.file = fileContainer
        fileObject.name = fileObject.file.name.slice(0, 100) + extension
        if (extension === '' || extension === '.dcm' || /^[0-9.]+$/.test(fileObject.name)) {
          fileObject.isDICOM = true
        }
        if (fileObject.name.toLowerCase() === 'dicomdir') {
          fileObject.isDICOMDIR = true
        }
        fileObject.isFile = true
        break
      default:
        throw new Error('Undefined file upload format, upload is: ' + Object.prototype.toString.call(fileContainer))
    }

    resolve(fileObject)
  })
}

const createFolder = (name, endpoint, parentID, ignoreNotifications) => {
  let responseParam = 'document_id'
  switch (endpoint) {
    default:
    case 'directory':
      responseParam = 'directory_id'
      break
    case 'study':
      name = name || 'study-' + new Date().getTime()
      responseParam = 'study_id'
      break
    case 'containers':
      name = name || 'images-' + new Date().getTime()
      responseParam = 'container_id'
      break
    case 'dicom_vault':
      name = name || 'images-' + new Date().getTime()
      responseParam = 'dicom_vault_id'
      break
  }
  name = name || 'images-' + new Date().getTime()
  return new Promise((resolve, reject) => {
    q.push({
      name: name,
      endpoint: endpoint,
      parentID: parentID,
      responseParam: responseParam,
      ignoreNotifications: ignoreNotifications
    }, function (err, data) {
      $state.folders = true
      if (err) {
        reject(err)
      } else {
        resolve(data)
      }
    })
  })
}

const uploadDocument = (item, parentID, asReport, ignoreNotifications) => {
  const remote = store.getters['session/getSelectedRemote']
  if (remote) {
    return uploadDocumentVault(item, asReport, parentID, ignoreNotifications, remote)
  }
  let uploadableFile = {
    file: item.file,
    params: {
      parent_id: parentID,
      file_name: item.name,
      is_report: asReport,
      ignore_notifications: ignoreNotifications
    },
    endpoint: 'document'
  }
  return $state.addFile(uploadableFile).then(() => {
    if ($state.error402) {
      throw new Error('No more storage left')
    }
  })
}

const uploadAvatar = (item) => {
  let uploadableFile = {
    file: item.file,
    params: {
      file_name: item.name
    },
    endpoint: item.endpoint
  }
  return $state.addFile(uploadableFile)
}

const uploadDICOM = (item, containerID) => {
  let uploadableFile = {
    file: item.file,
    params: {},
    endpoint: 'containers/' + containerID + '/dicom'
  }
  return $state.addFile(uploadableFile).then(() => {
    if ($state.error402) {
      throw new Error('No more storage left')
    }
  })
}

const uploadDICOMVault = async (item, studyID, ignoreNotifications) => {
  const studyUID = await getDICOMStudyUID(item.file)
  if (!studyUID && DICOMSTUDY_WHITELISTED_EXTENSIONS.indexOf(path.extname(item.name)) > -1) {
    return uploadDocument(item, studyID, ignoreNotifications)
  }

  const remote = store.getters['session/getSelectedRemote']
  const existsStudyUID = cache.get(store.state.session.accountId + remote.remote_id + remote.scope + studyUID + studyID)

  if (!existsStudyUID) {
    await Api.postV2('dicom_vault', {
      data: {
        parent_id: studyID,
        name: t('ImagesName', {name: studyUID}),
        ignore_notifications: ignoreNotifications,
        url: remote.url,
        scope: remote.scope,
        objectUID: studyUID,
        mode: 'update'
      }
    }).then(response => {
      if (response.body) {
        cache.set(store.state.session.accountId + remote.remote_id + remote.scope + studyUID + studyID, true, 60)
      }
    })
  }

  return $state.addFile({
    file: item.file,
    params: {},
    endpoint: 'vault/studies'
  })
}

const uploadDocumentVault = async (item, asReport, parentID, ignoreNotifications, remote) => {
  const completeDocumentVault = (response) => {
    if (!response || response.length === 0) {
      const err = new Error('No response')
      return Promise.reject(err)
    }

    const uploadUUID = response[0]
    return Api.postV2('document_vault', {
      data: {
        parent_id: parentID,
        name: item.name,
        ignore_notifications: ignoreNotifications,
        url: remote.url,
        scope: remote.scope,
        objectUID: uploadUUID,
        mode: 'create',
        is_report: asReport
      }
    })
  }
  return $state.addFile({
    file: item.file,
    // params: {}, vault params ?
    endpoint: 'vault/document',
    callback: completeDocumentVault
  })
}

const processDICOMSTUDY = (item, studyID, containerID, ignoreNotifications) => {
  let promises = []
  if (item.isFolder) {
    for (let child of item.children) {
      promises.push(processDICOMSTUDY(child, studyID, containerID, ignoreNotifications))
    }
  }
  if (item.isFile && item.isValid) {
    if (item.isDICOM) {
      if (store.getters['session/getSelectedRemote']) {
        promises.push(uploadDICOMVault(item, studyID, ignoreNotifications))
      } else {
        promises.push(uploadDICOM(item, containerID))
      }
    } else {
      if (DICOMSTUDY_WHITELISTED_EXTENSIONS.indexOf(path.extname(item.name)) > -1) {
        promises.push(uploadDocument(item, studyID, ignoreNotifications))
      }
    }
  }

  return Promise.all(promises)
}
const countDicomChildren = (item, count = 0) => {
  if (item.children && item.children.length > 0) {
    for (let i = 0; i < item.children.length; i++) {
      if (item.children[i].isDICOM) {
        count = count + 1
      }
      if (item.children[i].children && item.children[i].children.length > 0) {
        count = countDicomChildren(item.children[i], count)
      }
    }
  }
  return count
}
const processItem = (item, parentID, mode, asReport, ignoreNotifications) => {
  let promises = []
  if (item.isValid) {
    if ( // Three ways to enter en DICOM MODE
      (mode === BUNDLEDICOM && item.childrenHasDICOM) || // Mode is BUNDLEDICOM and any child has dicom files
      (mode === DICOM && item.hasDICOM) || // Mode is DICOM and has direct dicom children
      (mode === IGNOREDICOM && item.hasDICOMDIR) // Mode is not IGNOREDICOM and this folder is root of a dicom study
    ) {
      Api.getV2('files/upload-limit' + '?sul=' + sul)
        .then(response => {
          const limit = response.body.upload_limit
          if (countDicomChildren(item) > limit) {
            store.dispatch('dialog/showDialog', {
              title: t('NoUploadableTitle'),
              body: require('~/components/common/dialog/types/DialogNoUploadable.vue').default,
              persistent: true,
              props: { 'limit': limit },
              width: 480,
              actions: {
                accept: {
                  title: 'Close'
                }
              }
            })
            return
          }
          let name = item.name || 'study-' + new Date().getTime()
          promises.push(createFolder(name, 'study', parentID, false).then((study_id) => {
            if (store.getters['session/getSelectedRemote']) {
              promises.push(processDICOMSTUDY(item, study_id, null, false))
            } else {
              promises.push(createFolder(t('ImagesName', { name }), 'containers', study_id, true).then((container_id) => {
                promises.push(processDICOMSTUDY(item, study_id, container_id, false))
              }))
            }
          }))
        }).catch(error => {
          console.warn('ERROR', error)
          store.dispatch('feedback/addCatchSnack', error)
        })
    } else {
      if (item.isFolder) {
        promises.push(createFolder(item.name, 'directory', parentID, ignoreNotifications).then((file_id) => {
          for (let child of item.children) {
            promises.push(processItem(child, file_id, mode, false, false))
          }
        }))
      }
    }
    if (item.isFile) {
      promises.push(uploadDocument(item, parentID, asReport, ignoreNotifications))
    }
  }
  return Promise.all(promises)
}
let sul = ''
bus.$on('sul', (v) => {
  sul = v
})

let creationEventsEmitted = false
export default async function (payload) {
  const fileContainer = payload.files
  const parentID = payload.params ? payload.params.parent_id || 0 : 0
  const isReport = payload.params ? payload.params.is_report || false : false
  const endpoint = payload.endpoint || ''
  const reload = payload.reload

  if (endpoint.startsWith('account')) {
    return uploadAvatar({
      endpoint: endpoint,
      file: fileContainer
    })
  }

  let files = await preProcessFilesContainer(fileContainer)
  let dicomLimit = await Api.getV2('files/upload-limit' + '?sul=' + sul)
    .then(response => response.body.upload_limit).catch(error => {
      console.warn('ERROR', error)
      store.dispatch('feedback/addCatchSnack', error)
    })
  $state.$emit('uploadStart', reload)

  let uploadMode = IGNOREDICOM
  if (files.childrenHasDICOM || files.hasDICOM) {
    uploadMode = UNKNOWN
    let preferred = parseInt(store.state.session.preferredDICOMUpload)
    if (preferred > 0 && preferred < 2) {
      uploadMode = preferred
      if (preferred === DICOM && !files.hasFolders) {
        uploadMode = BUNDLEDICOM
      }
    }
    if (uploadMode === UNKNOWN) {
      uploadMode = await new Promise((resolve, reject) => {
        store.dispatch('dialog/showDialog', {
          title: t('Put DICOM files in a study'),
          body: require('~/components/common/dialog/types/DialogIncludeDICOM.vue').default,
          persistent: true,
          props: {disableDICOM: files.nestLevel <= 2},
          width: 800,
          actions: {
            accept: {
              title: 'Continue',
              callback: (mode) => {
                resolve(mode)
              }
            }
          }
        })
      })
    }
  }

  let promises = []
  if (uploadMode === BUNDLEDICOM && (files.childrenHasDICOM || files.hasDICOM)) {
    let name = files.name || 'study-' + new Date().getTime()
    if (countDicomChildren(files) > dicomLimit) {
      store.dispatch('dialog/showDialog', {
        title: t('NoUploadableTitle'),
        body: require('~/components/common/dialog/types/DialogNoUploadable.vue').default,
        persistent: true,
        props: { 'limit': dicomLimit },
        width: 480,
        actions: {
          accept: {
            title: 'Close'
          }
        }
      })
      return
    }
    promises.push(createFolder(name, 'study', parentID, false).then((study_id) => {
      $state.$emit('directoriesCreated', {files, reload, uploadMode, parentID})
      if (store.getters['session/getSelectedRemote']) {
        promises.push(processDICOMSTUDY(files, study_id, 0, false).then(() => {
          $state.$emit('filesCreated', {files, reload, uploadMode, parentID})
        }).catch(e => {
          console.log(e)
        }))
      } else {
        promises.push(createFolder(t('ImagesName', { name }), 'containers', study_id, true).then((container_id) => {
          $state.$emit('directoriesCreated', {files, reload, uploadMode, parentID: study_id})
          promises.push(processDICOMSTUDY(files, study_id, container_id, false).then(() => {
            $state.$emit('filesCreated', {files, reload, uploadMode, parentID: container_id})
          }).catch(e => {
            console.log(e)
          }))
        }).catch(e => {
          console.log(e)
        }))
      }
    }).finally(() => {
      creationEventsEmitted = true
    }))
  } else {
    if (
      (uploadMode === BUNDLEDICOM && files.childrenHasDICOM) || // Mode is BUNDLEDICOM and any child has dicom files
      (uploadMode === DICOM && files.hasDICOM) || // Mode is DICOM and has direct dicom children
      (uploadMode === IGNOREDICOM && files.hasDICOMDIR) // Mode is not IGNOREDICOM and this folder is root of a dicom study
    ) {
      promises.push(processItem(files, parentID, uploadMode, isReport, true))
    } else {
      for (let item of files.children) {
        if (countDicomChildren(item) > dicomLimit) {
          store.dispatch('dialog/showDialog', {
            title: t('NoUploadableTitle'),
            body: require('~/components/common/dialog/types/DialogNoUploadable.vue').default,
            persistent: true,
            props: { 'limit': dicomLimit },
            width: 480,
            actions: {
              accept: {
                title: 'Close'
              }
            }
          })
          break
        }
        promises.push(processItem(item, parentID, uploadMode, isReport, false))
      }
    }
  }

  q.drain(() => {
    $state.folders = false
  })

  return Promise.all(promises).then(() => {
    if (!creationEventsEmitted) {
      $state.$emit('directoriesCreated', {files, reload, uploadMode, parentID})
      $state.$emit('filesCreated', {files, reload, uploadMode, parentID})
    }
  })
}

const getDICOMStudyUID = (file) => {
  const reader = new FileReader()
  let resolveUID = () => {}
  let studyUID = new Promise((resolve, reject) => {
    resolveUID = resolve
  })
  reader.onload = function (event) {
    const byteArray = new Uint8Array(event.target.result)
    try {
      const dataSet = dicomParser.parseDicom(byteArray, {untilTag: 'x0020000d'})
      resolveUID(dataSet.string('x0020000d'))
    } catch (error) {
      console.log(error)
      resolveUID('')
    }
  }

  reader.readAsArrayBuffer(file)
  return studyUID.then((uid) => uid)
}



// WEBPACK FOOTER //
// ./src/plugins/uploader/methods/upload.js